/**
 * @fileoverview Client-side experiment assignment store (fetch-based bootstrap).
 * @description Assignments are fetched once per page load from
 * `GET /api/experiments/assignments` instead of being server-streamed into the
 * page. This keeps the homepage FULLY static: any PPR runtime Suspense hole on
 * `/` (server-streamed assignments or a streamed CTA slot) intermittently
 * desynced React's hydration matching in production builds (cascading React
 * #418 full-page recoveries, observed as duplicated layout chrome in Firefox
 * CI) — reproduced at ~50%/load with ANY runtime hole present and 0/10+ with
 * none. Until that framework-level issue is fixed upstream, no experiment
 * surface may reintroduce a runtime hole on a prerendered route.
 *
 * @module lib/experiments/assignments-client-store
 */

import type { ExperimentAssignment } from "./dimensions"

/** Response shape of `GET /api/experiments/assignments`. */
export interface AssignmentsResponse {
  assignments: ExperimentAssignment[]
  ledgerIngestToken?: string
  /** Serialized (encrypted when FLAGS_SECRET set) values for Vercel FlagValues. */
  flagValues?: string | Record<string, unknown>
}

interface StoreState {
  fetched: boolean
  assignments: readonly ExperimentAssignment[]
  ledgerIngestToken: string | undefined
  flagValues: string | Record<string, unknown> | undefined
}

let state: StoreState = {
  fetched: false,
  assignments: [],
  ledgerIngestToken: undefined,
  flagValues: undefined,
}

let inflight: Promise<void> | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) {
    listener()
  }
}

/**
 * Subscribes to store changes (for `useSyncExternalStore`).
 *
 * @param {() => void} onChange - Change callback.
 * @returns {() => void} Unsubscribe.
 */
export function subscribeAssignments(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => listeners.delete(onChange)
}

/**
 * True once the fetch completed — the point at which an EMPTY assignment set
 * becomes an authoritative "nothing is running" (safe to clear persisted
 * dimensions) rather than "not loaded yet".
 *
 * @returns {boolean} Whether assignments are authoritatively known.
 */
export function areAssignmentsFetched(): boolean {
  return state.fetched
}

/**
 * Reads the fetched assignments (empty until the fetch resolves).
 *
 * @returns {readonly ExperimentAssignment[]} Current assignments.
 */
export function getFetchedAssignments(): readonly ExperimentAssignment[] {
  return state.assignments
}

/**
 * Reads the visitor-bound ledger ingest token from the fetch.
 *
 * @returns {string | undefined} HMAC token when minted.
 */
export function getFetchedLedgerIngestToken(): string | undefined {
  return state.ledgerIngestToken
}

/**
 * Reads serialized flag values for Vercel Analytics correlation.
 *
 * @returns {string | Record<string, unknown> | undefined} FlagValues payload.
 */
export function getFetchedFlagValues():
  | string
  | Record<string, unknown>
  | undefined {
  return state.flagValues
}

/**
 * Fetches assignments once per page load (deduped across callers).
 *
 * @returns {Promise<void>} Resolves when the store is populated (or the
 *   fetch failed — analytics must never break UX, so failures leave the
 *   store un-fetched and experiments simply don't report this page load).
 */
export function ensureAssignmentsFetched(): Promise<void> {
  if (typeof window === "undefined" || state.fetched) {
    return Promise.resolve()
  }
  return refreshAssignments()
}

/**
 * Re-fetches assignments even when a fetch already completed. Exists for
 * ledger ingest token ROTATION: the token carries a 24h expiry, and an SPA
 * tab open past it kept POSTing the stale token (the store treated the first
 * fetch as final), so the guard 403'd and the Postgres ledger silently
 * missed conversions GA4 still recorded (#233 `r3517282446`). Deduped with
 * any in-flight fetch; a successful refresh REPLACES assignments and token.
 *
 * @returns {Promise<void>} Resolves when the refresh settles (best-effort).
 */
export function refreshAssignments(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve()
  }
  if (inflight) {
    return inflight
  }
  inflight = fetch("/api/experiments/assignments", {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  })
    .then(async (response) => {
      if (!response.ok) {
        return
      }
      const body = (await response.json()) as AssignmentsResponse
      state = {
        fetched: true,
        assignments: Array.isArray(body.assignments) ? body.assignments : [],
        ledgerIngestToken:
          typeof body.ledgerIngestToken === "string" &&
          body.ledgerIngestToken.length > 0
            ? body.ledgerIngestToken
            : undefined,
        flagValues: body.flagValues,
      }
      notify()
    })
    .catch(() => {
      // Best-effort: leave the store un-fetched.
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

/**
 * How long a pre-fetch click waits for assignments before emitting anyway.
 * The assignments GET is a fast pure read; past this, the event's value
 * (with experiment dimensions) is outweighed by the risk of losing it entirely.
 */
export const FAST_CLICK_FLUSH_TIMEOUT_MS = 2000

/**
 * Runs `emit` once assignments are fetched (or immediately if they already
 * are), with a timeout fallback so a failed/slow fetch can't swallow the event.
 * Shared by the hero CTA and footer experiment click handlers: a first-load
 * click can beat the assignments fetch, and emitting immediately would carry no
 * experiment dimensions (dropping exactly the fastest converters from the
 * assigned arm). The caller pairs this with holding navigation for plain clicks
 * so the deferred emit still resolves against the click surface's pathname.
 *
 * @param {() => void} emit - Event emitter to run exactly once.
 * @returns {void}
 */
export function emitWhenAssignmentsReady(emit: () => void): void {
  if (areAssignmentsFetched()) {
    emit()
    return
  }
  let done = false
  const runOnce = (): void => {
    if (done) {
      return
    }
    done = true
    unsubscribe()
    clearTimeout(timer)
    emit()
  }
  const unsubscribe = subscribeAssignments(runOnce)
  const timer = setTimeout(runOnce, FAST_CLICK_FLUSH_TIMEOUT_MS)
  // Kick (or join) the fetch; on failure the store never notifies and the
  // timeout emits — same dimensionless event as before this guard, never worse.
  void ensureAssignmentsFetched()
}

/**
 * Test-only reset.
 *
 * @returns {void}
 */
export function __resetAssignmentsStoreForTests(): void {
  state = {
    fetched: false,
    assignments: [],
    ledgerIngestToken: undefined,
    flagValues: undefined,
  }
  inflight = null
  listeners.clear()
}
